// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.6.14-XM80lRoYRUdkHMpUfMaX/mode=imports,min/optimized/keen-slider.js'

const { config } = window.BAO.Carousel
const { setInitialised, addWheelControls } = window.BAO.Carousel.plugins
const productDetailHtmlCache = new Map()
const executableScriptTypes = new Set([
  '',
  'module',
  'text/javascript',
  'application/javascript',
])
let lastInitialisedVariantId = null
let pendingCarouselAbsIdx = null
class ProductDetailColourCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slides', 'slide', 'link']
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.els?.slide?.elements?.length < 2) return

    if (this.offsetWidth > 0) {
      this.initialiseCarousel()
    } else {
      this.visibilityObserver = new ResizeObserver(() => {
        if (this.offsetWidth === 0) return
        this.visibilityObserver.disconnect()
        this.initialiseCarousel()
      })
      this.visibilityObserver.observe(this)
    }

    this.setupPrefetchObserver()
  }

  initialiseCarousel () {
    this.carousel = this.setupCarousel()
    this.syncCarouselPosition()
    this.updateCarouselOverflow()
    this.reinitOrderDeadlineApp()
  }

  setupListeners () {
    super.setupListeners()

    this.handleColourClick = this.handleColourClick.bind(this)
    this.handlePopState = this.handlePopState.bind(this)

    this.els.link.elements.forEach(link => {
      this.listeners.add(link, 'click', this.handleColourClick)
    })

    if (this.els?.arrow?.exists) {
      this.handleArrowClick = this.handleArrowClick.bind(this)
      this.els.arrow.elements.forEach(arrow =>
        this.listeners.add(arrow, 'click', this.handleArrowClick),
      )
    }

    window.addEventListener('popstate', this.handlePopState)
  }

  handleArrowClick (event) {
    const direction = event.currentTarget?.dataset?.direction
    if (!this.carousel || !direction) return

    const { rel, maxIdx } = this.carousel.track.details
    const offset = direction === 'previous' ? -3 : 3
    const targetIndex = Math.max(0, Math.min(rel + offset, maxIdx))

    this.carousel.moveToIdx(targetIndex)

    event.currentTarget.blur()
  }

  updateCarouselOverflow () {
    const { rel, maxIdx } = this.carousel?.track?.details || {}
    if (rel === undefined || maxIdx === undefined) return

    this.classList.toggle(
      'prd-DetailColourVariants_Carousel-overflowStart',
      rel > 0,
    )
    this.classList.toggle(
      'prd-DetailColourVariants_Carousel-overflowEnd',
      rel < maxIdx,
    )

    this.els?.arrow?.elements?.forEach(arrow => {
      const isPrevious = arrow.dataset.direction === 'previous'
      arrow.disabled = isPrevious ? rel <= 0 : rel >= maxIdx
    })
  }

  async handleColourClick (event) {
    const link = event.target.closest(this.els.link.selector)
    if (!link || !this.contains(link)) return

    const productUrl = this.getProductUrl(link)
    if (!productUrl) return

    event.preventDefault()

    await this.swapDetailBlocks(productUrl)
  }

  // Update current-item classes on carousels that live outside the replaced columns block (e.g. mobile)
  syncExternalCarousels (productUrl, replacedColumns) {
    const allCarousels = document.querySelectorAll(
      'product-detail-colour-carousel',
    )
    const currentClass = 'prd-DetailColourVariants_Item-current'

    allCarousels.forEach(carousel => {
      if (replacedColumns.contains(carousel)) return

      carousel.querySelectorAll('[data-product-url]').forEach(slide => {
        const url = slide.dataset.productUrl
        slide.classList.toggle(
          currentClass,
          url === productUrl || url === productUrl.split('?')[0],
        )
      })
    })
  }

  setupPrefetchObserver () {
    if (!('IntersectionObserver' in window)) {
      this.prefetchOnIdle()
      return
    }

    this.prefetchObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          this.prefetchOnIdle()
          this.prefetchObserver?.disconnect()
        })
      },
      { rootMargin: '0px 0px 800px 0px', threshold: 0 },
    )

    this.prefetchObserver.observe(this)
  }

  prefetchOnIdle () {
    const productUrls = this.getProductUrls()
    if (!productUrls.length) return

    const schedule = window.requestIdleCallback
      ? window.requestIdleCallback
      : cb => window.setTimeout(cb, 0)

    schedule(() => {
      productUrls.forEach(productUrl =>
        this.prefetchProductDetailHtml(productUrl),
      )
    })
  }

  async swapDetailBlocks (productUrl, shouldPushState = true) {
    if (this.isSwapping) return
    this.isSwapping = true
    pendingCarouselAbsIdx = this.carousel?.track?.details?.abs ?? null

    try {
      const html = await this.getProductDetailHtml(productUrl)
      if (!html) return

      const newDoc = new DOMParser().parseFromString(html, 'text/html')

      const newColumns = newDoc.querySelector(
        '[data-product-detail-el="columns"]',
      )
      const currentColumns = document.querySelector(
        '[data-product-detail-el="columns"]',
      )
      if (!newColumns || !currentColumns) return

      this.stripNoscriptStyles(newColumns)
      this.pinColumnHeights(currentColumns, newColumns)
      this.replaceColumnsWithoutScrollJump(
        currentColumns,
        newColumns,
        productUrl,
      )

      const currentMobileColumns = document.querySelector(
        '.prd-Detail_Column-mobile',
      )
      const newMobileColumns = newDoc.querySelector('.prd-Detail_Column-mobile')
      if (currentMobileColumns && newMobileColumns) {
        this.stripNoscriptStyles(newMobileColumns)

        this.replaceColumnsWithoutScrollJump(
          currentMobileColumns,
          newMobileColumns,
          productUrl,
        )
      }

      pendingCarouselAbsIdx = null

      if (shouldPushState) {
        window.history.pushState({ path: productUrl }, '', productUrl)
      }

      const nextTitle = newDoc.querySelector('title')?.textContent
      if (nextTitle) document.title = nextTitle

      this.updatePreOrderModal(productUrl)
      this.updateProductBundleDrawer(productUrl)
      this.updateShopTheLookDrawer(newDoc)
    } finally {
      this.isSwapping = false
    }
  }

  // <script> tags carried over via DOMParser/innerHTML never execute (their
  // "already started" flag is set on parse) — so any component whose module
  // wasn't already loaded on the page we swapped FROM (e.g. product-detail-sets.js
  // when the previous colour had no sets) never gets its customElements.define
  // call, and stays a dead, un-upgraded element. Re-creating each script tag
  // makes the browser fetch/evaluate it for real; type="module" scripts are
  // deduped by the browser's own module map, so this is a no-op for scripts
  // that are already loaded.
  executeScripts (container) {
    if (!container) return

    container.querySelectorAll('script').forEach(oldScript => {
      if (!executableScriptTypes.has(oldScript.type)) return

      const newScript = document.createElement('script')
      ;[...oldScript.attributes].forEach(attr => {
        newScript.setAttribute(attr.name, attr.value)
      })
      newScript.textContent = oldScript.textContent

      oldScript.replaceWith(newScript)
    })
  }

  // Stamps the current measured heights onto the incoming columns so there is
  // no layout shift when the DOM is replaced. Only pins elements that are
  // actually visible — hidden elements (e.g. desktop columns on mobile) return
  // offsetHeight 0 and are skipped
  pinColumnHeights (currentColumns, newColumns) {
    const selectors = [
      '.prd-Detail_ColumnMiddle', // desktop media column
      '.prd-Detail_Colours', // swatch row (desktop right column)
    ]

    selectors.forEach(sel => {
      const current = currentColumns.querySelector(sel)
      const next = newColumns.querySelector(sel)
      if (current && next && current.offsetHeight > 0) {
        next.style.minHeight = `${current.offsetHeight}px`
      }
    })
  }

  // Swaps the columns node without a visible scroll jump, then releases the
  // document-level height pin once the carousel has settled.
  replaceColumnsWithoutScrollJump (currentColumns, newColumns, productUrl) {
    // Pin document height before the swap. iOS Safari silently ignores scrollTo
    // if the target exceeds the document height — which can happen when the new
    // content is initially shorter before images expand.
    document.documentElement.style.minHeight = `${document.documentElement.scrollHeight}px`

    // Force a synchronous layout so the minHeight is committed before replaceWith.
    // Without this, iOS can batch both the style change and the DOM mutation
    // together and apply minHeight AFTER it has already clamped scrollY.
    this.forceLayout()

    currentColumns.replaceWith(newColumns)
    this.executeScripts(newColumns)

    // No manual scroll restore here — iOS Safari naturally preserves scroll
    // position through DOM mutations. Calling scrollTo immediately after
    // replaceWith fights the browser's own anchoring and causes a visible jump.

    // Gallery images are lazy but the user is scrolled past them — flip to
    // eager so they fetch immediately rather than waiting for scroll-up.
    this.eagerLoadCarouselImages(newColumns)

    // Safari won't paint images in an off-screen overflow:hidden container.
    // Briefly scroll the gallery into view so Safari renders it, then restore.

    // *** Temporarily disabled to test if it's still needed. ***
    // this.flushGalleryRender(newColumns)

    this.syncExternalCarousels(productUrl, newColumns)

    window.requestAnimationFrame(() => {
      document.querySelector('product-detail')?.cloneContent?.()
    })

    // Release the document-level floor and the column-level minHeights (set by
    // pinColumnHeights) once the carousel has settled. Keeping the column pin
    // until now prevents the media column from collapsing on iOS while lazy
    // images above the fold are pending; releasing it afterwards avoids a
    // permanent gap when the new variant has fewer images than the old one.
    this.waitForCarouselReady(newColumns).then(() => {
      window.requestAnimationFrame(() => {
        document.documentElement.style.minHeight = ''
        ;['.prd-Detail_ColumnMiddle', '.prd-Detail_Colours'].forEach(sel => {
          const column = newColumns.querySelector(sel)
          if (column) column.style.minHeight = ''
        })
      })
    })
  }

  // Reads offsetHeight to force a synchronous layout flush. Used to ensure
  // CSS writes (e.g. minHeight) are committed before a DOM mutation fires.
  forceLayout () {
    return document.documentElement.offsetHeight
  }

  // On Safari, scroll the media carousel into full view after a colour swap
  // so the browser paints the gallery images immediately.
  flushGalleryRender (columns) {
    if (
      navigator.userAgentData ||
      !/^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    ) {
      return
    }

    const carousel = columns.querySelector('product-media-carousel')
    if (!carousel) return

    carousel.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Flips lazy images in the new media carousel to eager and bumps their fetch
  // priority, then schedules a background decode so iOS Safari has them fully
  // rasterized before the user scrolls up to the gallery.
  eagerLoadCarouselImages (columns) {
    const imgs = [...columns.querySelectorAll('product-media-carousel img')]
    const decodePromises = imgs.map(img => {
      if (img.loading === 'lazy') img.loading = 'eager'
      img.fetchPriority = 'high'
      return img.decode ? img.decode().catch(() => {}) : Promise.resolve()
    })

    const mediaCarousel = columns.querySelector('product-media-carousel')
    if (mediaCarousel) {
      Promise.all(decodePromises).then(() => {
        window.requestAnimationFrame(() => {
          mediaCarousel.carousel?.update?.()
        })
      })
    }
  }

  // Resolves once the Swiper carousel has finished re-initialising and its
  // dimensions have stopped changing (150ms debounce). A 1200ms hard cap
  // covers cases where the carousel is off-screen and the ResizeObserver
  // never fires.
  waitForCarouselReady (columns) {
    const carousel = columns.querySelector('product-media-carousel')
    if (!carousel) return Promise.resolve()

    return new Promise(resolve => {
      let settlementTimer

      const settle = () => {
        clearTimeout(settlementTimer)
        settlementTimer = setTimeout(() => {
          observer.disconnect()
          resolve()
        }, 150)
      }

      const observer = new ResizeObserver(settle)
      observer.observe(carousel)
      settle()

      setTimeout(() => {
        clearTimeout(settlementTimer)
        observer.disconnect()
        resolve()
      }, 1200)
    })
  }

  async updateProductBundleDrawer (productUrl) {
    if (!this.externalEls?.bundleDrawer?.exists) return

    const productHandle = this.getProductHandleFromUrl(productUrl)
    if (!productHandle) return

    const { productBundleDrawerCache } = window.BAO.Caches
    const html = await productBundleDrawerCache.get(productHandle)
    if (!html) return

    const parent = this.externalEls.bundleDrawer.element.parentElement
    if (!parent) return

    const newDoc = new DOMParser().parseFromString(html, 'text/html')
    const newDrawer = newDoc.querySelector('.prd-BundleDrawer_Inner')
    if (!newDrawer) return

    parent.innerHTML = newDrawer.outerHTML
    this.executeScripts(parent)
  }

  // The shop-the-look drawer lives outside [data-product-detail-el="columns"],
  // so it isn't touched by the columns swap above and is refreshed here from
  // the already-fetched newDoc instead.
  updateShopTheLookDrawer (newDoc) {
    if (!this.externalEls?.shopTheLookDrawer?.exists) return

    const newDrawer = newDoc.querySelector('shop-the-look-drawer')
    if (!newDrawer) return

    const parent = this.externalEls.shopTheLookDrawer.element.parentElement
    if (!parent) return

    parent.innerHTML = newDrawer.outerHTML
    this.executeScripts(parent)
  }

  async updatePreOrderModal (productUrl) {
    if (!this.externalEls?.form?.exists) return

    const productHandle = this.getProductHandleFromUrl(productUrl)
    if (!productHandle) return

    const { preOrderRequestFormCache } = window.BAO.Caches
    const html = await preOrderRequestFormCache.get(productHandle)
    if (!html) return

    const parent = this.externalEls.form.element.parentElement
    if (!parent) return

    const newDoc = new DOMParser().parseFromString(html, 'text/html')
    const newForm = newDoc.querySelector('.prd-PreOrder_Inner')
    if (!newForm) return

    parent.innerHTML = newForm.outerHTML
    this.executeScripts(parent)
  }

  async handlePopState (event) {
    const productUrl = event?.state?.path || this.getUrlFromLocation()
    if (!productUrl) return

    await this.swapDetailBlocks(productUrl, false)
  }

  // DOMParser parses with scripting disabled, so <noscript> content becomes real
  // elements instead of staying inert text — any noscript stylesheet would apply
  // for real once these columns land in the live, scripted document.
  stripNoscriptStyles (columnsEl) {
    if (!columnsEl) return

    columnsEl.querySelectorAll('noscript').forEach(el => el.remove())
  }

  syncCarouselPosition () {
    if (!this.carousel) return

    const { maxIdx, slides } = this.carousel.track.details

    if (pendingCarouselAbsIdx !== null) {
      const targetIdx = Math.min(pendingCarouselAbsIdx, maxIdx)

      if (targetIdx > 0) {
        this.carousel.moveToIdx(targetIdx, true, { duration: 0 })
      }

      return
    }

    const currentIdx = this.carousel.slides.findIndex(slide =>
      slide.classList.contains('prd-DetailColourVariants_Item-current'),
    )
    if (currentIdx <= 0) return

    const visibleCount = slides.filter(slide => slide.portion === 1).length
    const targetIdx = Math.min(
      Math.max(0, currentIdx - Math.floor(visibleCount / 2)),
      maxIdx,
    )

    if (targetIdx > 0) {
      this.carousel.moveToIdx(targetIdx, true, { duration: 0 })
    }
  }

  reinitOrderDeadlineApp () {
    const currentSlide = this.carousel?.slides?.find(slide =>
      slide.classList.contains('prd-DetailColourVariants_Item-current'),
    )

    const variantId = currentSlide?.dataset?.variantId
    if (!variantId || variantId === lastInitialisedVariantId) return

    lastInitialisedVariantId = variantId
    this.waitForOrderDeadlineApp(() => this.populateOrderDeadlineApp())
  }

  waitForOrderDeadlineApp (callback, attempts = 0) {
    if (window.orderDeadlineAppApi?.init && window.orderDeadlineApp) {
      return callback()
    }

    if (attempts >= 5) return

    setTimeout(() => this.waitForOrderDeadlineApp(callback, attempts + 1), 1000)
  }

  populateOrderDeadlineApp () {
    const currentSlide = this.carousel.slides.find(slide =>
      slide.classList.contains('prd-DetailColourVariants_Item-current'),
    )
    if (!currentSlide) return

    const {
      productId,
      variantId,
      productOverrides,
      variantOverrides,
      variantInventoryManagement,
      variantInventoryPolicy,
      variantInventoryData,
      collectionIds,
    } = currentSlide.dataset

    const parsedCollectionIds = collectionIds?.split(',') || []
    const parsedProductOverrides = this.safeJson(productOverrides)
    const parsedVariantOverrides = this.safeJson(variantOverrides)

    // The app's global objects are sealed/read-only, so each must be replaced.
    Object.assign(window.orderDeadlineApp, {
      collectionOverrides: this.buildCollectionOverrides(
        parsedCollectionIds,
        currentSlide,
      ),
      productCollectionIds: { [productId]: parsedCollectionIds.map(Number) },
      productOverrides: Object.keys(parsedProductOverrides).length
        ? { [productId]: parsedProductOverrides }
        : {},
      variantInventoryData: {
        [variantId]: {
          inventoryManagement: variantInventoryManagement,
          inventoryPolicy: variantInventoryPolicy,
          inventoryQuantity: Number(variantInventoryData),
        },
      },
      variantOverrides: Object.keys(parsedVariantOverrides).length
        ? { [variantId]: parsedVariantOverrides }
        : {},
    })

    window.orderDeadlineAppApi.init()
  }

  buildCollectionOverrides (collectionIds, slide) {
    return collectionIds.reduce((acc, collectionId) => {
      const raw = slide.dataset[`collection-${collectionId}Override`]
      if (!raw) return acc
      acc[collectionId] = this.safeJson(raw)
      return acc
    }, {})
  }

  getProductUrls () {
    return this.els.link.elements
      .map(link => this.getProductUrl(link))
      .filter(Boolean)
  }

  getProductUrl (link) {
    const slide = link.closest(this.els.slide.selector)
    return slide?.dataset?.productUrl || link.getAttribute('href')
  }

  getUrlFromLocation () {
    return (
      window.location.pathname + window.location.search + window.location.hash
    )
  }

  async getProductDetailHtml (productUrl) {
    if (productDetailHtmlCache.has(productUrl)) {
      return productDetailHtmlCache.get(productUrl)
    }

    const html = await this.fetchProductDetailHtml(productUrl)
    if (html) productDetailHtmlCache.set(productUrl, html)
    return html
  }

  async fetchProductDetailHtml (productUrl) {
    try {
      const sectionId = this.getSectionId() || 'template-product'
      const sections = await window.BAO.sectionRenderer.fetch(
        sectionId,
        productUrl,
      )
      return sections?.[sectionId] || ''
    } catch (e) {
      console.error('Error fetching section:', e)
      return ''
    }
  }

  async prefetchProductDetailHtml (productUrl) {
    if (productDetailHtmlCache.has(productUrl)) return

    await Promise.all([
      this.getProductDetailHtml(productUrl),
      this.prefetchPreOrderForm(productUrl),
      this.prefetchProductBundleDrawer(productUrl),
    ])
  }

  async prefetchProductBundleDrawer (productUrl) {
    if (!this.externalEls?.bundleDrawer?.exists) return

    const productHandle = this.getProductHandleFromUrl(productUrl)
    if (!productHandle) return

    const { productBundleDrawerCache } = window.BAO.Caches
    return productBundleDrawerCache.get(productHandle)
  }

  async prefetchPreOrderForm (productUrl) {
    if (!this.externalEls?.form?.exists) return

    const productHandle = this.getProductHandleFromUrl(productUrl)
    if (!productHandle) return

    const { preOrderRequestFormCache } = window.BAO.Caches
    return preOrderRequestFormCache.get(productHandle)
  }

  getProductHandleFromUrl (productUrl) {
    const match = productUrl.match(/\/products\/([^/?#]+)/)
    return match ? match[1] : null
  }

  getSectionId () {
    return document.querySelector('product-detail')?.dataset?.sectionId
  }

  setupCarousel () {
    return new KeenSlider(this.els.slides.element, this.options(), [
      setInitialised(),
      addWheelControls('horizontal'),
    ])
  }

  options () {
    return {
      selector: this.els.slide.selector,
      loop: false,
      rubberband: false,
      mode: 'snap',
      defaultAnimation: {
        duration: config.defaultAnimation.duration,
      },
      dragSpeed: config.dragSpeed,
      detailsChanged: () => this.updateCarouselOverflow(),
      slides: {
        perView: 'auto',
        spacing: 15,
      },
      breakpoints: {
        '(min-width: 768px)': {
          slides: {
            perView: 'auto',
            spacing: 18,
          },
        },
        '(min-width: 1200px)': {
          slides: {
            perView: 'auto',
            spacing: 15,
          },
        },
      },
    }
  }

  safeJson (raw) {
    try {
      return raw ? JSON.parse(raw) : {}
    } catch (e) {
      console.error('Failed to parse JSON', e)
      return {}
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('popstate', this.handlePopState)
  }
}

if (!customElements.get('product-detail-colour-carousel')) {
  customElements.define(
    'product-detail-colour-carousel',
    ProductDetailColourCarousel,
  )
}
